/**
 * PostHog analytics wrapper.
 *
 * Activates when `VITE_POSTHOG_KEY` is set. All exports are no-ops
 * when the SDK hasn't been initialised, so feature code can call
 * unconditionally (same pattern as `errorTracking.ts`).
 *
 * What this file wires up:
 *
 *   1. Init — autocapture ON (allow-listed via data-attrs for PII
 *      safety), rageclick + dead-click detection, capture_pageview
 *      off (we drive it ourselves from react-router).
 *   2. identify() — user id + email + role + createdAt as person
 *      properties. Called on auth resolve from useAuth.
 *   3. groupTenant() — associates the current session with a tenant
 *      so PostHog Group Analytics panels (retention by tenant,
 *      cohort by tenant size, etc.) become available. Called once
 *      useProfile loads in AppShell.
 *   4. track() — fire-and-forget event capture. Standard event list
 *      documented below.
 *   5. trackPageview() — called from <PageviewTracker> on every
 *      react-router location change.
 *   6. featureFlagEnabled() — sync read of a PostHog feature flag.
 *   7. showSurvey() / dismissSurvey() — PostHog renders eligible
 *      surveys automatically; these helpers let us show a specific
 *      survey on demand (e.g. after N successful Pacioli answers).
 *
 * Events convention: `<domain>_<verb>` in snake_case. Current set:
 *
 *   - pacioli_question_sent   — Pacioli question submitted
 *   - galter_question_sent    — Galter question submitted
 *   - citation_clicked        — user opened a [Источник N] chip
 *   - agent_switched          — user toggled Galter ↔ Pacioli
 *   - signup_completed        — new account /signup flow finished
 *   - login_completed         — existing /login flow finished
 *   - feedback_submitted      — in-app feedback button POSTed
 *   - $pageview               — emitted by <PageviewTracker>
 *   - $autocapture            — auto-emitted by the SDK on clicks
 *                               against elements marked with
 *                               `data-ph-capture`
 */

import posthog, { PostHog } from 'posthog-js';
import { CONSENT_EVENT, readConsent, type ConsentEventDetail } from './consent';

let ph: PostHog | null = null;
let initAttempted = false;
// Set once we've attached the consent-wait listener so we don't
// pile on multiple listeners across repeated initAnalytics() calls.
let consentListenerBound = false;

const APP_VERSION = (import.meta.env.VITE_BUILD_ID as string | undefined) || 'dev';

export function initAnalytics(): void {
  if (initAttempted) return;

  // GDPR gate: third-party analytics must stay dormant until the
  // user has accepted the cookie banner. `declined` → never init;
  // `null` → init on the first `altai-consent: accepted` event.
  const consent = readConsent();
  if (consent !== 'accepted') {
    if (consent === 'declined') return;
    if (consentListenerBound || typeof window === 'undefined') return;
    consentListenerBound = true;
    const onConsent = (evt: Event) => {
      const detail = (evt as CustomEvent<ConsentEventDetail>).detail;
      if (detail?.value !== 'accepted') return;
      window.removeEventListener(CONSENT_EVENT, onConsent);
      // Re-entry path: initAttempted is still false because we
      // bailed out above, so the real init body runs this time.
      initAnalytics();
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    return;
  }

  initAttempted = true;

  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return;

  const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

  posthog.init(key, {
    api_host: host,

    // We emit $pageview manually from <PageviewTracker> so SPA
    // transitions register consistently. $pageleave lets PostHog
    // measure time-on-page without us doing anything.
    capture_pageview: false,
    capture_pageleave: true,

    // Autocapture is on, but narrowed: only elements tagged with
    // `data-ph-capture` (or their descendants) generate events.
    // This guards against leaking inline CopyToClipboardField
    // contents, form values, 1C record IDs, etc. — the default
    // autocapture is too greedy for a tax-data product.
    autocapture: {
      dom_event_allowlist: ['click', 'submit'],
      element_allowlist: ['a', 'button', 'form', 'input'],
      css_selector_allowlist: ['[data-ph-capture]', '[data-ph-capture] *'],
    },

    // Rageclicks + dead clicks are genuine UX telemetry. PostHog
    // detects them in autocapture; no extra flag needed beyond
    // autocapture being on.

    // Person profiles are created only when we call identify() —
    // anonymous traffic still gets events but no person row, which
    // keeps the MTU count honest.
    person_profiles: 'identified_only',

    // Session replay — sample 10% of sessions permanently, plus record
    // 100% of sessions where an error is captured (triggered below).
    // Inputs masked by default to prevent accidental token capture;
    // any element with `data-sensitive` has its text masked too.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-sensitive], .citation-body, .answer-body',
      sampleRate: 0.1,
    },
    disable_session_recording: false,

    // Persist distinct id across localStorage + cookie. Needed so
    // anon users who land on the marketing site, click Signup, and
    // eventually identify still share a single distinct id.
    persistence: 'localStorage+cookie',
    cookie_name: 'ph_altai',

    // Forward $current_url so the funnel inspectors resolve URLs
    // even when the server rewrites react-router paths.
    property_blacklist: [],

    loaded: (instance) => {
      // Super properties — stamped onto every event. Kept small:
      // only things that don't change within a session.
      instance.register({
        app: 'altai-web',
        app_version: APP_VERSION,
        release: APP_VERSION,
        commit_sha: APP_VERSION,
        environment:
          (import.meta.env.VITE_ENVIRONMENT as string | undefined) ||
          (import.meta.env.PROD ? 'production' : 'development'),
        language:
          typeof navigator !== 'undefined' ? navigator.language?.split('-')[0] || 'en' : 'en',
      });
    },

    // Opt the SDK into capturing the "Do Not Track" header. Events
    // aren't captured if the user's browser sends DNT=1.
    respect_dnt: true,
  });

  ph = posthog;
}

// ─── Event capture ─────────────────────────────────────────────────

export function track(event: string, properties?: Record<string, unknown>): void {
  if (!ph) return;
  ph.capture(event, properties);
}

export function trackPageview(url?: string): void {
  if (!ph) return;
  ph.capture('$pageview', url ? { $current_url: url } : undefined);
}

// ─── Identity ──────────────────────────────────────────────────────

export function identify(
  userId: string,
  traits?: {
    email?: string;
    name?: string;
    tenantId?: string;
    role?: string;
    createdAt?: string;
  },
): void {
  if (!ph) return;
  const personProperties: Record<string, unknown> = {};
  if (traits?.email) personProperties.email = traits.email;
  if (traits?.name) personProperties.name = traits.name;
  if (traits?.role) {
    personProperties.role = traits.role;
    personProperties.is_admin = traits.role === 'ADMIN';
  }
  if (traits?.tenantId) personProperties.tenant_id = traits.tenantId;
  if (traits?.createdAt) personProperties.signed_up_at = traits.createdAt;
  // Best-effort client-observable onboarding flag; the authoritative
  // value is set from the backend PostHog identify().
  try {
    if (typeof window !== 'undefined') {
      personProperties.has_onboarded = window.localStorage?.getItem('altai.onboarded') === '1';
    }
  } catch {
    /* localStorage unavailable */
  }
  ph.identify(userId, personProperties);
}

/**
 * Associate the current distinct id with a tenant so PostHog's
 * Group Analytics features become available. Call once after
 * useProfile resolves the tenant id.
 *
 * Property object is persisted on the group row in PostHog so
 * filters like "tenants on Free plan" become available.
 */
export function groupTenant(tenantId: string, properties?: Record<string, unknown>): void {
  if (!ph) return;
  ph.group('tenant', tenantId, properties);
}

export function resetAnalytics(): void {
  if (!ph) return;
  ph.reset();
}

// ─── Feature flags ─────────────────────────────────────────────────

export function featureFlagEnabled(key: string): boolean {
  if (!ph) return false;
  const value = ph.getFeatureFlag(key);
  return value === true || value === 'true';
}

export function featureFlagPayload<T = unknown>(key: string): T | null {
  if (!ph) return null;
  return (ph.getFeatureFlagPayload(key) as T) ?? null;
}

// ─── Surveys ───────────────────────────────────────────────────────

/**
 * Show a specific PostHog survey by id. Eligible surveys (targeting
 * rules matched) already render automatically via the SDK — use
 * this when we want to kick off a survey from app logic, e.g. after
 * the user successfully submits their first Pacioli answer.
 */
export function showSurvey(surveyId: string): void {
  // The SDK's internal api is named `renderSurvey`; typing isn't
  // exported. Cast through unknown to avoid an `any`.
  if (!ph) return;
  const fn = (
    ph as unknown as {
      renderSurvey?: (id: string, selector?: string) => void;
    }
  ).renderSurvey;
  if (typeof fn === 'function') fn.call(ph, surveyId);
}

export const analyticsEnabled = (): boolean => ph !== null;
